import React from 'react';
import { GuideOpenBody, GuideTip, GuideTipKind } from '../../../../../interfaces/stay-guide';
import { StayKey, t } from '../../../../onboarding/stay/stay-strings';
import { GuideH1 } from './guide-frame';
import { langOf, mapsHref, telHref } from './guide-format';
import {
  HostText,
  Muted,
  Row,
  SecondaryAnchor,
  SectionHeading,
  SmallHeading,
  Stack,
  TipCard,
  TipList,
  TipPhoto,
} from './guide.styled';

const L = 'en' as const;

/** The groups, in this order, with their headings (contract §4). */
export const TIP_GROUPS: Array<{ kind: GuideTipKind; label: StayKey }> = [
  { kind: 'eat', label: 'guide.kind_eat' },
  { kind: 'see', label: 'guide.kind_see' },
  { kind: 'nearby', label: 'guide.kind_nearby' },
  { kind: 'transport', label: 'guide.kind_transport' },
  { kind: 'other', label: 'guide.kind_other' },
];

const TipEntry: React.FC<{ tip: GuideTip; city: string; lang: string | undefined }> = ({ tip, city, lang }) => {
  const nameId = `guide-tip-${tip.id}`;
  const maps = mapsHref(tip.maps_url, [tip.name, tip.address || city].filter(Boolean).join(', '));
  const tel = telHref(tip.phone);

  return (
    <TipCard aria-labelledby={nameId} data-guide-tip={tip.kind}>
      {tip.photo ? <TipPhoto src={tip.photo.url} alt="" /> : null}
      <SmallHeading id={nameId} lang={lang}>
        {tip.name}
      </SmallHeading>
      {tip.note ? <HostText lang={lang}>{tip.note}</HostText> : null}
      {tip.address ? <Muted lang={lang}>{tip.address}</Muted> : null}
      {maps || tel ? (
        <Row>
          {maps ? (
            <SecondaryAnchor href={maps} target="_blank" rel="noreferrer noopener" aria-describedby={nameId}>
              {t(L, 'guide.openInMaps')}
            </SecondaryAnchor>
          ) : null}
          {tel ? (
            <SecondaryAnchor href={tel} aria-describedby={nameId}>
              {tip.phone}
            </SecondaryAnchor>
          ) : null}
        </Row>
      ) : null}
    </TipCard>
  );
};

/**
 * LOCAL TIPS (plan S7.7): the host's places, grouped Eat & drink → See & do →
 * Nearby → Getting around → More. "Open in Maps" is the host's link, else a
 * search for the place's name and address (or the building's city). With no
 * tips the hub has no card for this page, so the empty line is only met by a
 * deep link.
 */
export const GuideLocal: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const lang = langOf(body.guide.content_language);
  const groups = TIP_GROUPS.map(g => ({ ...g, tips: body.tips.filter(tip => tip.kind === g.kind) })).filter(
    g => g.tips.length > 0
  );

  return (
    <>
      <GuideH1>
        {t(L, 'guide.localTitle')}
      </GuideH1>
      {groups.length ? (
        groups.map(g => (
          <Stack as="section" key={g.kind} aria-labelledby={`guide-tips-${g.kind}`} data-guide-group={g.kind}>
            <SectionHeading id={`guide-tips-${g.kind}`}>{t(L, g.label)}</SectionHeading>
            <TipList>
              {g.tips.map(tip => (
                <TipEntry key={tip.id} tip={tip} city={body.guide.building_city} lang={lang} />
              ))}
            </TipList>
          </Stack>
        ))
      ) : (
        <Muted role="status">{t(L, 'guide.localEmpty')}</Muted>
      )}
    </>
  );
};
